import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

export default function AboutPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-black flex flex-col">
      <Navigation />

      <div className="flex-1 pt-28 pb-20 px-6">
        <div className="max-w-3xl mx-auto">

          {/* Hero */}
          <div className="text-center mb-20">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Institutional-grade compute, without the institutional overhead
            </h1>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto leading-relaxed">
              We're infrastructure and analytics people from cloud providers, financial
              institutions, and data center OEMs — building the GPU platform we wished existed
              when we were the ones architecting solutions, running workloads, and fighting
              the infrastructure to do it.
            </p>
          </div>

          {/* Story */}
          <div className="mb-20">
            <div className="space-y-6 text-gray-400 leading-relaxed">
              <p>
                If you've ever tried to GPU-accelerate a backtest, train a model on tick data, or
                run a Monte Carlo simulation across parameter space, you know the real bottleneck
                isn't the math — it's the infrastructure. Getting a properly configured GPU instance
                with the right CUDA version, the right drivers, and the right frameworks is a
                project in itself. Doing it cost-effectively across clouds is another one entirely.
              </p>
              <p>
                We've spent our careers on both sides of that problem — designing and
                deploying GPU workloads as cloud architects at major providers, and building
                the analytics pipelines and workloads that depend on that compute. We've seen how much time gets burned
                navigating provider consoles, negotiating quota increases with support teams,
                debugging driver mismatches, and comparing pricing across regions and clouds.
                Time that should be spent on alpha research.
              </p>
              <p>
                PropAlgos puts GPU-accelerated strategy development, backtesting, and ML training
                behind a single interface. We negotiate volume pricing with cloud providers,
                maintain pre-configured images with RAPIDS, PyTorch, and the quant libraries you
                actually use, and surface real-time availability and cost across AWS and Google
                Cloud. AI-assisted tooling helps you move faster from hypothesis to result. You
                pick a GPU, we handle the rest.
              </p>
              <p>
                Whether you're an experienced quant looking to iterate faster without a DevOps
                team, or you're just getting started and want access to the same hardware the
                prop desks use — this is what we built PropAlgos for.
              </p>
            </div>
          </div>

          {/* What we believe */}
          <div className="mb-20">
            <h2 className="text-2xl font-bold text-white mb-10 text-center">What we believe</h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
              <div className="text-center">
                <h3 className="text-lg font-medium text-white mb-2">Compute should be accessible</h3>
                <p className="text-gray-400 text-sm">
                  You shouldn't need a cloud engineering team to run a GPU-accelerated backtest.
                  The barrier to quantitative research should be the ideas, not the infrastructure.
                </p>
              </div>

              <div className="text-center">
                <h3 className="text-lg font-medium text-white mb-2">Pricing should be transparent</h3>
                <p className="text-gray-400 text-sm">
                  Real-time, per-minute billing. No hidden fees, no surprise invoices. You see
                  the cost before you launch and while you run.
                </p>
              </div>

              <div className="text-center">
                <h3 className="text-lg font-medium text-white mb-2">Infrastructure should disappear</h3>
                <p className="text-gray-400 text-sm">
                  The best infrastructure is the kind you don't have to think about. Launch a
                  notebook, run your strategy, shut it down. Everything else is our problem.
                </p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center">
            <button
              onClick={() => navigate('/login')}
              className="px-8 py-4 bg-white hover:bg-gray-100 text-black font-medium rounded-lg transition-all duration-200"
            >
              Get Started
            </button>
          </div>

        </div>
      </div>

      <Footer />
    </div>
  );
}
